import { useEffect, useMemo, useState, type ComponentType, type ReactNode, type SelectHTMLAttributes, type SVGProps } from "react";
import { cn } from "../../lib/cn";
import { useIsDesktop } from "../../lib/hooks";
import { IconBack, IconChevronDown, IconChevronRight, IconSearch } from "../icons";
import { IconButton } from "./Button";

/*
 * Shared building blocks for settings screens (account settings, community settings,
 * channel settings): a searchable sidebar, titled groups of rows, and compact controls.
 */

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export interface SettingsNavItem<T extends string = string> {
  id: T;
  label: string;
  icon?: Icon;
  /** Extra words that match the sidebar search. */
  keywords?: string;
  danger?: boolean;
  /** Small trailing element, e.g. an external-link icon. */
  trailing?: ReactNode;
}

export interface SettingsNavGroup<T extends string = string> {
  title?: string;
  items: SettingsNavItem<T>[];
}

/**
 * Two-pane settings layout. Desktop: sidebar + scrolling content. Mobile: the list,
 * then a full-screen section with a back button.
 */
export function SettingsLayout<T extends string>({
  title,
  titleIcon: TitleIcon,
  header,
  groups,
  active,
  onSelect,
  footer,
  children,
  sectionTitle,
  onBack,
  searchPlaceholder = "Search settings",
  wide,
  onClose,
  search = true,
}: {
  title: ReactNode;
  titleIcon?: Icon;
  /** Replaces the default title block above the search box. */
  header?: ReactNode;
  groups: SettingsNavGroup<T>[];
  active: T | undefined;
  onSelect: (id: T) => void;
  /** Pinned to the bottom of the sidebar (e.g. sign out). */
  footer?: ReactNode;
  children: ReactNode;
  sectionTitle?: ReactNode;
  /** Mobile only: return to the section list. */
  onBack?: () => void;
  searchPlaceholder?: string;
  /** Let the content use more width (tables, two-column editors). */
  wide?: boolean;
  /** Shows a close button (and Esc) for full-screen settings such as a channel's. */
  onClose?: () => void;
  /** Hide the sidebar search for short section lists. */
  search?: boolean;
}) {
  const desktop = useIsDesktop();
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector("[role=dialog],[role=menu]")) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) return t.blur();
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((i) => `${i.label} ${i.keywords ?? ""} ${g.title ?? ""}`.toLowerCase().includes(needle)) }))
      .filter((g) => g.items.length);
  }, [groups, q]);

  const nav = (
    <nav aria-label="Settings sections" className={cn("flex min-h-0 flex-col", desktop ? "h-full w-64 shrink-0 border-r border-line bg-bg-subtle" : "h-full w-full")}>
      <div className="shrink-0 px-3 pt-3">
        {header ?? (
          <div className="flex h-10 items-center gap-2 px-1.5">
            {TitleIcon && <TitleIcon className="icon size-[18px] text-fg-2" aria-hidden />}
            <h1 className="truncate text-base font-semibold">{title}</h1>
          </div>
        )}
        <div className={cn("relative mt-2", !search && "hidden")}>
          <IconSearch className="icon pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="h-8 w-full rounded-md border border-line bg-surface pl-8 pr-2 text-sm text-fg placeholder:text-fg-3 focus:border-accent focus:outline-none"
          />
        </div>
      </div>
      <div className="scroll-y min-h-0 flex-1 px-3 pb-3 pt-2">
        {filtered.map((g, gi) => (
          <div key={g.title ?? gi} className={cn(gi > 0 && "mt-3 border-t border-line pt-3")}>
            {g.title && <div className="px-2 pb-1 text-xs font-medium text-fg-3">{g.title}</div>}
            <ul className="space-y-px">
              {g.items.map((i) => {
                const on = active === i.id;
                return (
                  <li key={i.id}>
                    <button
                      onClick={() => {
                        onSelect(i.id);
                        setQ("");
                      }}
                      aria-current={on ? "page" : undefined}
                      className={cn(
                        "flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-left text-sm transition-colors",
                        i.danger ? "text-danger hover:bg-danger-soft" : on ? "bg-surface-2 text-fg" : "text-fg-2 hover:bg-surface hover:text-fg",
                      )}
                    >
                      {i.icon && <i.icon className="icon size-[18px] shrink-0" aria-hidden />}
                      <span className="min-w-0 flex-1 truncate">{i.label}</span>
                      {i.trailing}
                      {!desktop && !i.danger && <IconChevronRight className="icon size-4 text-fg-3" aria-hidden />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {filtered.length === 0 && <p className="px-2 py-6 text-center text-sm text-fg-3">No settings match “{q}”.</p>}
      </div>
      {footer && <div className="shrink-0 border-t border-line p-3">{footer}</div>}
    </nav>
  );

  const content = (
    <div className="scroll-y relative min-h-0 min-w-0 flex-1">
      {onClose && (
        <div className="sticky top-0 z-20 float-right flex flex-col items-center gap-0.5 p-3 md:p-6">
          <button
            onClick={onClose}
            aria-label="Close settings"
            className="flex size-9 items-center justify-center rounded-full border border-line-strong text-fg-2 transition-colors hover:border-fg-3 hover:text-fg"
          >
            <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          <span className="hidden text-[11px] font-medium text-fg-3 md:block">Esc</span>
        </div>
      )}
      <div className={cn("px-4 pb-16 pt-4 md:px-10 md:pt-8", wide ? "max-w-6xl" : "max-w-3xl")}>
        {(sectionTitle || (!desktop && onBack)) && (
          <header className="mb-5 flex min-h-9 items-center gap-2">
            {!desktop && onBack && <IconButton label="Back" icon={IconBack} onClick={onBack} className="-ml-2" />}
            {sectionTitle && <h2 className="text-xl font-semibold">{sectionTitle}</h2>}
          </header>
        )}
        {children}
      </div>
    </div>
  );

  if (desktop)
    return (
      <div className="flex h-full">
        {nav}
        {content}
      </div>
    );
  return <div className="flex h-full">{active ? content : nav}</div>;
}

/** A titled group of rows inside a subtle bordered container. */
export function SettingsGroup({ title, description, action, children, className }: { title?: ReactNode; description?: ReactNode; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn("mb-7", className)}>
      {(title || action) && (
        <div className="mb-2 flex items-end justify-between gap-4 px-0.5">
          <div className="min-w-0">
            {title && <h3 className="text-sm font-semibold text-fg-2">{title}</h3>}
            {description && <p className="mt-0.5 text-xs text-fg-3">{description}</p>}
          </div>
          {/* Negative margin so an action doesn't push the title out of line with neighbouring groups. */}
          {action && <div className="-my-1.5 shrink-0">{action}</div>}
        </div>
      )}
      <div className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-bg-subtle">{children}</div>
    </section>
  );
}

/** One row: label + optional description on the left, control on the right. */
export function SettingsRow({
  title,
  description,
  children,
  htmlFor,
  icon: I,
  stack,
  disabled,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  htmlFor?: string;
  icon?: Icon;
  /** Put the control under the label (wide controls on narrow screens). */
  stack?: boolean;
  disabled?: boolean;
}) {
  return (
    <div className={cn("flex gap-x-6 gap-y-2.5 px-4 py-3", stack ? "flex-col" : "items-center justify-between max-sm:flex-wrap", disabled && "opacity-60")}>
      {/* basis-0 + a minimum width: small controls (switches) stay beside the label on
          narrow screens, wide ones (selects, segmented controls) wrap underneath. */}
      <label htmlFor={htmlFor} className={cn("flex min-w-0 items-start gap-3", !stack && "flex-1 basis-0 max-sm:min-w-[min(100%,12rem)]")}>
        {I && <I className="icon mt-0.5 size-[18px] shrink-0 text-fg-3" aria-hidden />}
        <span className="min-w-0">
          <span className="block text-sm font-medium text-fg">{title}</span>
          {description && <span className="mt-0.5 block text-xs text-fg-3">{description}</span>}
        </span>
      </label>
      {children !== undefined && <div className={cn("flex shrink-0 items-center gap-2", stack && "w-full")}>{children}</div>}
    </div>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

/** Styled native select: keyboard and screen reader support for free. */
export function Select<T extends string>({
  value,
  onChange,
  options,
  className,
  ...rest
}: { value: T; onChange: (v: T) => void; options: SelectOption<T>[]; className?: string } & Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange">) {
  return (
    <span className={cn("relative inline-flex", className)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="h-8 w-full min-w-36 appearance-none rounded-md border border-line-strong bg-surface pl-3 pr-8 text-sm text-fg hover:border-fg-3/40 focus:border-accent focus:outline-none disabled:opacity-50"
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <IconChevronDown className="icon pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
    </span>
  );
}

/** Sticky bar shown when a form has unsaved changes (Discord-style), with reset and save. */
export function UnsavedBar({ show, saving, onReset, onSave }: { show: boolean; saving?: boolean; onReset: () => void; onSave: () => void }) {
  if (!show) return null;
  return (
    <div className="sticky bottom-4 z-10 mt-6 flex items-center justify-between gap-3 rounded-lg bg-surface-2 py-2 pl-4 pr-2 shadow-pop animate-slide-up">
      <span className="text-sm text-fg-2">You have unsaved changes</span>
      <div className="flex gap-2">
        <button onClick={onReset} className="h-8 rounded-md px-3 text-sm text-fg-2 hover:bg-surface-3 hover:text-fg">
          Reset
        </button>
        <button onClick={onSave} disabled={saving} className="h-8 rounded-md bg-primary px-3 text-sm font-medium text-fg-inverse hover:bg-primary-hover disabled:opacity-50">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
